import { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { BulkStatusResult, TravelDetailInput } from '../../types/readyToFly';

interface BulkAssignFlightDialogProps {
  open: boolean;
  applicationCount: number;
  errorMessage: string | null;
  resultSummary: BulkStatusResult | null;
  onClose: () => void;
  onSubmit: (input: TravelDetailInput & { departureDate?: string; arrivalDate?: string }) => Promise<void>;
}

export const BulkAssignFlightDialog = ({
  open,
  applicationCount,
  errorMessage,
  resultSummary,
  onClose,
  onSubmit,
}: BulkAssignFlightDialogProps) => {
  const [airline, setAirline] = useState('');
  const [flightNumber, setFlightNumber] = useState('');
  const [departureAirport, setDepartureAirport] = useState('');
  const [arrivalAirport, setArrivalAirport] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        airline: airline || undefined,
        flightNumber: flightNumber || undefined,
        departureAirport: departureAirport || undefined,
        arrivalAirport: arrivalAirport || undefined,
        departureDate: departureDate || undefined,
        arrivalDate: arrivalDate || undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Assign flight to {applicationCount} student(s)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {resultSummary && (
            <Alert severity={resultSummary.failed === 0 ? 'success' : 'warning'}>
              Updated {resultSummary.succeeded}/{resultSummary.total} ({resultSummary.failed} failed)
            </Alert>
          )}
          <TextField label="Airline" fullWidth value={airline} onChange={(e) => setAirline(e.target.value)} />
          <TextField
            label="Flight number"
            fullWidth
            value={flightNumber}
            onChange={(e) => setFlightNumber(e.target.value)}
          />
          <TextField
            label="Departure airport"
            fullWidth
            value={departureAirport}
            onChange={(e) => setDepartureAirport(e.target.value)}
          />
          <TextField
            label="Arrival airport"
            fullWidth
            value={arrivalAirport}
            onChange={(e) => setArrivalAirport(e.target.value)}
          />
          <TextField
            label="Departure date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={departureDate}
            onChange={(e) => setDepartureDate(e.target.value)}
          />
          <TextField
            label="Arrival date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={arrivalDate}
            onChange={(e) => setArrivalDate(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={submitting} onClick={handleSubmit}>
          {submitting ? 'Saving…' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
